import {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  resetSalesTargetDisplayContentMedia,
  setSalesTargetDisplayContent,
  setSalesTargetDisplayContentMedia
} from '@/store/slices/salesTargetSlice';
import {useSalesTargets} from '@/hooks/useSalesTargets';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';
import {IMAGETYPE} from '@/utils/constants/saleTargetConstants';
import {ROUTES} from '@/utils/constants/routesConstants';
import {getStepConfig} from '@/utils/salesTargetStepConfig';
import {logger} from '@/helpers/logger';
import {updateLanguageFieldMap} from '@/pages/PerfectStoreUtils';
import {
  buildSalesTargetSimpleDisplayContentFormData,
  hasSalesTargetSimpleDisplayFormError
} from '@/containers/DisplayContent/salesTargetSimpleDisplayShared';

/**
 * Shared create/edit state for single-image sales-target Display pages
 * (Rack Challenge and CDA Super Target). Page-specific UI (image size,
 * preview types, copy) stays in each page so Rack visuals/behavior are unchanged.
 */
export const useSalesTargetSimpleDisplayContent = ({
  targetType,
  draftErrorMessage
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const countryCode = useAppSelector((state) => state.countryCode)?.countryCode;
  const commonLanguages = useAppSelector((state) => state.common?.languages);
  const salesTarget = useAppSelector((state) => state.salesTarget);
  const {handleDraft} = useSalesTargets(false);

  const isEdit = location.state?.isEdit ?? false;
  const disableDraft = location.state?.disableDraft ?? false;
  const salesTargetIdFromState = location.state?.salesTargetId;

  const getNavigationState = useCallback(
    () => ({
      isEdit,
      disableDraft,
      editPublishedBeforeStart: disableDraft,
      salesTargetId: salesTargetIdFromState || salesTarget?.id
    }),
    [isEdit, disableDraft, salesTargetIdFromState, salesTarget?.id]
  );

  const languages = useMemo(
    () => COUNTRY_LANGUAGES_CODES[countryCode] || commonLanguages || [],
    [countryCode, commonLanguages]
  );

  const [targetImage, setTargetImage] = useState(null);

  useEffect(() => {
    const media = salesTarget?.displayContentMedia;
    const imageUri = media?.imageUri || media?.imageUrl;
    if (imageUri) {
      setTargetImage({
        fileName: media?.title,
        imageUri: media?.imageUri,
        imageUrl: media?.imageUrl
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImageChange = useCallback(
    (image) => {
      setTargetImage(image);
      dispatch(
        setSalesTargetDisplayContentMedia({
          title: image?.fileName || '',
          imageId: IMAGETYPE.Foreground,
          imageUri: image?.imageUrl || image?.imageUri || '',
          imageUrl: image?.imageUrl || '',
          optIn: false
        })
      );
    },
    [dispatch]
  );

  const handleImageDelete = useCallback(() => {
    setTargetImage(null);
    dispatch(resetSalesTargetDisplayContentMedia());
  }, [dispatch]);

  const [displayContent, setDisplayContent] = useState({});

  useEffect(() => {
    if (!languages?.length) return;
    const hydrated = {};
    languages.forEach((lang) => {
      hydrated[lang.code] = {...salesTarget?.displayContent?.[lang.code]};
    });
    setDisplayContent(hydrated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languages]);

  const [isCancel, setIsCancel] = useState(false);
  const [imageExpanded, setImageExpanded] = useState(true);

  const handleContentChange = useCallback(
    (langCode, field, value, hasError) => {
      setDisplayContent((prev) =>
        updateLanguageFieldMap(prev, langCode, field, value, hasError)
      );
    },
    []
  );

  const buildDisplayContentFormData = useCallback(
    () =>
      buildSalesTargetSimpleDisplayContentFormData({
        languages,
        displayContent,
        targetType: salesTarget?.targetType
      }),
    [languages, displayContent, salesTarget?.targetType]
  );

  const handleCancel = () => setIsCancel((prev) => !prev);

  const onConfirmCancel = () => {
    setIsCancel(false);
    navigate(ROUTES.SALES_TARGETS);
  };

  const handleDraftAction = async () => {
    try {
      const displayContentFormData = buildDisplayContentFormData();
      const response = await handleDraft(
        salesTarget,
        salesTarget,
        false,
        'displayContent',
        {displayContentFormData}
      );
      if (response?.success) {
        navigate(ROUTES.SALES_TARGETS);
      }
    } catch (error) {
      logger.error(draftErrorMessage, error);
    }
  };

  const handleNextAction = () => {
    dispatch(setSalesTargetDisplayContent(buildDisplayContentFormData()));
    navigate(ROUTES.SALES_TARGET_REVIEW, {state: getNavigationState()});
  };

  const getFormError = () =>
    hasSalesTargetSimpleDisplayFormError({
      targetImage,
      languages,
      displayContent
    });

  const stepConfig = getStepConfig(targetType);
  const previewImage = targetImage?.imageUri || targetImage?.imageUrl || '';

  const handleStepClick = (step) => {
    const stepRouteMap = {
      1: ROUTES.SALES_TARGET_ADD,
      3: ROUTES.SALES_TARGET_REVIEW
    };
    const route = stepRouteMap[step];
    if (route) {
      navigate(route, {state: getNavigationState()});
    }
  };

  return {
    languages,
    targetImage,
    previewImage,
    displayContent,
    isCancel,
    imageExpanded,
    setImageExpanded,
    disableDraft,
    stepConfig,
    handleImageChange,
    handleImageDelete,
    handleContentChange,
    handleCancel,
    onConfirmCancel,
    handleDraftAction,
    handleNextAction,
    handleStepClick,
    getFormError
  };
};
